/**
 * Preguntas sugeridas del chat del Brain — UNA lista.
 *
 * Hasta el 30/09/2026 vivían duplicadas en BrainChatPage.tsx (es/en/pt) y en el
 * smoke test de clientes (solo es), y "si cambian allá, cambian acá" nunca pasaba.
 * Las propias de cada cliente están en su JSON (`questions`); las genéricas de
 * cliente y las de las áreas están acá. Sin React: puro, lo importan el front, el
 * smoke test y los agentes.
 */
import { BRAIN_CLIENT_PUBLIC } from './brain-clients/public';
import { BRAIN_AREA_PREFIX } from './types/brain';
import { type AppLang, normalizeLang, pickByLang } from './lang';

/** Genéricas para TODO cliente (van después de las propias del cliente). */
export const BRAIN_GENERIC_SUGGESTIONS: Record<AppLang, string[]> = {
  es: [
    'Armame el handover de esta cuenta',
    'Sanity check del forecast vs lo conversado',
    '¿Qué proyectos ongoing hay en este cliente?',
  ],
  en: [
    'Create the handover doc for this account',
    'Sanity check the forecast vs what was discussed',
    'What ongoing projects does this client have?',
  ],
  pt: [
    'Monte o handover desta conta',
    'Sanity check do forecast vs o que foi conversado',
    'Quais projetos em andamento este cliente tem?',
  ],
};

/** Áreas (People, Finance): para quien no sabe ni qué preguntar (alguien en onboarding). Las genéricas de cliente NO aplican. */
export const BRAIN_AREA_SUGGESTIONS: Record<AppLang, Record<string, string[]>> = {
  es: {
    [`${BRAIN_AREA_PREFIX}people`]: [
      '¿Quién es mi líder y quiénes son de mi equipo?',
      '¿Cómo pido vacaciones o un día libre?',
      '¿Qué es EPIC y cuándo se hace?',
      '¿Qué es el D-Talk?',
      '¿Cómo funciona la revisión salarial?',
      '¿Cuáles son las clapbases y qué beneficios tienen?',
      '¿Qué herramientas de trabajo uso y cómo pido acceso?',
      '¿Con quién hablo si tengo un problema en mi equipo?',
    ],
    [`${BRAIN_AREA_PREFIX}finance`]: [
      '¿Cómo cerró el último mes contra el presupuesto?',
      '¿Qué línea del P&L explica el desvío de EBITDA?',
      '¿Cómo viene el revenue acumulado del año vs BGT?',
      '¿Qué business cases de Investment están más desviados?',
      '¿Cuánto pesó el tipo de cambio este mes?',
      '¿Cómo vienen los costos non-payroll vs el mes anterior?',
      '¿Cómo está el PI por pod en septiembre?',
      'Armame la matriz margen × productividad del último mes cerrado',
      '¿Qué cambió de V1 a V2 en el modelo de capacity de Content?',
      '¿Cuánta open capacity tiene cada líder de Content?',
      '¿Cuántos assets produjo Content el último mes y de qué tipo?',
      '¿Qué líderes no cerraron la carga de horas del mes?',
    ],
    [`${BRAIN_AREA_PREFIX}pipeline`]: [
      '¿Cómo venimos de cobertura de plan para el próximo trimestre?',
      '¿Qué gap tenemos en Q4 por segmento y cuántas propuestas hacen falta?',
      '¿Cuál es la conversión de Proposal a Won y cuánto tardamos en cerrar?',
      '¿Qué deals están en riesgo (cierre vencido o sin movimiento)?',
      '¿Qué deals concentran el pipeline ponderado?',
      '¿Cómo evolucionó el pipeline este mes: creados, ganados, perdidos?',
      '¿Qué fuentes del Investment Framework y qué partners generan los deals que ganamos?',
      '¿Se cumplió lo que el forecast decía hace 90 días?',
      '¿Qué oportunidades de los partners están paradas en el radar sin trabajar?',
      '¿Cómo viene el inbound del formulario web mes a mes?',
      '¿Qué POCs de clientes cambiaron de cargo en los últimos 90 días?',
      '¿Qué tier 1 de la hitlist no tocamos hace 90 días y tienen POC alcanzable?',
      '¿Qué clientes se nos están enfriando?',
    ],
  },
  en: {
    [`${BRAIN_AREA_PREFIX}people`]: [
      'Who is my leader and who is on my team?',
      'How do I request vacation or a day off?',
      'What is EPIC and when does it happen?',
      'What is the D-Talk?',
      'How does the salary review work?',
      'What are the clapbases and what benefits do they have?',
      'Which work tools do I use and how do I request access?',
      'Who do I talk to if I have a problem in my team?',
    ],
    [`${BRAIN_AREA_PREFIX}finance`]: [
      'How did the last month close against budget?',
      'Which P&L line explains the EBITDA deviation?',
      'How is year-to-date revenue vs budget?',
      'Which Investment business cases are most off plan?',
      'How much did FX weigh this month?',
      'How are non-payroll costs vs the previous month?',
      'How is the PI by pod for September?',
      'Build the margin × productivity matrix for the last closed month',
      'What changed from V1 to V2 in the Content capacity model?',
      'How much open capacity does each Content lead have?',
      'How many assets did Content produce last month, by type?',
      'Which leads have not closed the hour load for the month?',
    ],
    [`${BRAIN_AREA_PREFIX}pipeline`]: [
      'How is plan coverage looking for next quarter?',
      'What is the Q4 gap by segment and how many proposals do we need?',
      'What is the Proposal-to-Won conversion and how long do we take to close?',
      'Which deals are at risk (overdue close or no movement)?',
      'Which deals concentrate the weighted pipeline?',
      'How did the pipeline evolve this month: created, won, lost?',
      'Which Investment Framework sources and partners generate the deals we win?',
      'Did the forecast from 90 days ago come true?',
      'Which partner opportunities are sitting untouched on the radar?',
      'How is web-form inbound doing month by month?',
      'Which client POCs changed roles in the last 90 days?',
      'Which tier 1 hitlist accounts have gone 90 days untouched and have a reachable POC?',
      'Which clients are going cold on us?',
    ],
  },
  pt: {
    [`${BRAIN_AREA_PREFIX}people`]: [
      'Quem é meu líder e quem está no meu time?',
      'Como peço férias ou um dia de folga?',
      'O que é o EPIC e quando acontece?',
      'O que é o D-Talk?',
      'Como funciona a revisão salarial?',
      'Quais são as clapbases e que benefícios têm?',
      'Quais ferramentas de trabalho uso e como peço acesso?',
      'Com quem falo se tiver um problema no meu time?',
    ],
    [`${BRAIN_AREA_PREFIX}finance`]: [
      'Como fechou o último mês contra o orçamento?',
      'Qual linha do P&L explica o desvio de EBITDA?',
      'Como está a receita acumulada do ano vs BGT?',
      'Quais business cases de Investment estão mais fora do plano?',
      'Quanto pesou o câmbio este mês?',
      'Como estão os custos non-payroll vs o mês anterior?',
      'Como está o PI por pod em setembro?',
      'Monte a matriz margem × produtividade do último mês fechado',
      'O que mudou de V1 para V2 no modelo de capacity de Content?',
      'Quanta open capacity tem cada líder de Content?',
      'Quantos assets Content produziu no último mês, por tipo?',
      'Quais líderes não fecharam a carga de horas do mês?',
    ],
    [`${BRAIN_AREA_PREFIX}pipeline`]: [
      'Como está a cobertura de plano para o próximo trimestre?',
      'Qual é o gap do Q4 por segmento e quantas propostas faltam?',
      'Qual é a conversão de Proposal para Won e quanto demoramos para fechar?',
      'Quais deals estão em risco (fechamento vencido ou sem movimento)?',
      'Quais deals concentram o pipeline ponderado?',
      'Como o pipeline evoluiu este mês: criados, ganhos, perdidos?',
      'Quais fontes do Investment Framework e quais partners geram os deals que ganhamos?',
      'O forecast de 90 dias atrás se cumpriu?',
      'Quais oportunidades dos partners estão paradas no radar sem trabalhar?',
      'Como está o inbound do formulário web mês a mês?',
      'Quais POCs de clientes mudaram de cargo nos últimos 90 dias?',
      'Quais tier 1 da hitlist não tocamos há 90 dias e têm POC alcançável?',
      'Quais clientes estão esfriando?',
    ],
  },
};

/** Propias del cliente (de su JSON) en el idioma pedido; vacío si no tiene. */
export function brainClientQuestions(clientId: string, lang: string | null | undefined): string[] {
  const q = BRAIN_CLIENT_PUBLIC[clientId]?.questions;
  return q ? pickByLang(q, normalizeLang(lang)) : [];
}

/**
 * Lo que ve el chat al elegir un scope: las de un área, o las propias del cliente
 * seguidas de las genéricas. Sin scope, nada.
 */
export function brainSuggestionsFor(scopeId: string | null | undefined, lang: string | null | undefined): string[] {
  if (!scopeId) return [];
  const l = normalizeLang(lang);
  if (scopeId.startsWith(BRAIN_AREA_PREFIX)) return pickByLang(BRAIN_AREA_SUGGESTIONS, l)[scopeId] ?? [];
  return [...brainClientQuestions(scopeId, l), ...pickByLang(BRAIN_GENERIC_SUGGESTIONS, l)];
}
